import { AttentionBlock, Button, Kpi, KpiGrid, Skeleton, StatusTag } from '@westhetics/ui'
import type { ReactNode } from 'react'
import type { AttentionItem, OperationArea, OperationsOverview } from './api'
import {
  JOB_LABELS,
  JOB_NOTES,
  JOB_STATUS,
  PERIOD_PHRASES,
  attentionTitle,
  formatBytes,
  jobRunText,
} from './labels'

/**
 * Os blocos que saem de `GET /admin/operations` (RN-ADM-96, RN-ADM-97): o que pede atenção, primeiro, com
 * fio Plum; as rotinas de cada minuto; e os números do período, um bloco por frente, em grade de fio.
 */

const number = (n: number) => n.toLocaleString('pt-BR')

/** "O que pede atenção": uma linha por problema, a crítica antes, cada uma levando às falhas da área. */
export function AttentionList({
  items,
  onSeeFailures,
}: {
  items: AttentionItem[]
  onSeeFailures: (area: OperationArea) => void
}) {
  if (items.length === 0) return null
  return (
    <AttentionBlock variant="plum" title={attentionTitle(items.length)}>
      <ul className="divide-hairline divide-y" aria-label="O que pede atenção">
        {items.map((item) => (
          <li
            key={item.code}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2"
          >
            <span className={item.level === 'critical' ? 'text-plum' : undefined}>{item.text}</span>
            <Button
              variant="text"
              size="sm"
              onClick={() => onSeeFailures(item.area)}
              aria-label={`Ver as falhas: ${item.text}`}
            >
              ver as falhas
            </Button>
          </li>
        ))}
      </ul>
    </AttentionBlock>
  )
}

/** As três rotinas do agendador, com a última rodada e a situação (RN-ADM-96). */
export function JobsBlock({ jobs, now }: { jobs: OperationsOverview['jobs']; now: number }) {
  return (
    <section aria-labelledby="operations-jobs" className="flex flex-col gap-3">
      <h2 id="operations-jobs" className="eyebrow">
        Rotinas de cada minuto
      </h2>
      <ul className="border-hairline bg-surface divide-hairline divide-y border">
        {jobs.map((job) => {
          const status = JOB_STATUS[job.status]
          return (
            <li
              key={job.job}
              aria-label={JOB_LABELS[job.job]}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4"
            >
              <span className="flex min-w-0 flex-col gap-1">
                <span className="font-display text-ink text-[14px] leading-[1.3] font-normal">
                  {JOB_LABELS[job.job]}
                </span>
                <span className="text-secondary text-[12px] leading-[1.5] font-light">
                  {jobRunText(job, now)}
                </span>
                <span className="text-label text-[11px] leading-[1.5] font-light">
                  {JOB_NOTES[job.job]}
                </span>
              </span>
              <StatusTag level={status.level}>{status.label}</StatusTag>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** Um bloco da grade: o nome da frente, a linha de contexto (a conta, o provedor) e os números. */
function Front({
  title,
  context,
  children,
}: {
  title: string
  context?: ReactNode
  children: ReactNode
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h3 className="eyebrow">{title}</h3>
      {context ? (
        <p className="text-secondary text-[12px] leading-[1.5] font-light">{context}</p>
      ) : null}
      <KpiGrid>{children}</KpiGrid>
    </section>
  )
}

/** O número que é risco quando passa de zero ganha plum; os outros seguem preto. */
const risk = (n: number) => (n > 0 ? 'critical' : 'default')

/** Os números do período, um bloco por frente (o ECS, o RDS, a ConvertAPI e a fila do legado). */
export function NumbersBlock({ overview }: { overview: OperationsOverview }) {
  const { sends, autentique, pdf, email, integrations, database } = overview
  const events = autentique.events
  const connectionsHigh =
    database.maxConnections > 0 && database.connections / database.maxConnections > 0.8

  return (
    <section aria-labelledby="operations-numbers" className="flex flex-col gap-5">
      <h2 id="operations-numbers" className="eyebrow">
        Números {PERIOD_PHRASES[overview.period]}
      </h2>

      <Front title="Envios" context="WhatsApp e e-mail da Westhetics, fora a Autentique.">
        <Kpi label="Enviados" value={number(sends.sent)} note="saíram no período" />
        <Kpi
          label="Falharam"
          value={number(sends.failed)}
          tone={risk(sends.failed)}
          note={`${number(sends.failedByChannel.whatsapp)} por WhatsApp · ${number(sends.failedByChannel.email)} por e-mail`}
        />
        <Kpi label="Na fila" value={number(sends.queued)} note="agora; saem a cada minuto" />
        <Kpi label="Assistidos" value={number(sends.manual)} note="WhatsApp pela recepção" />
      </Front>

      <Front
        title="Autentique"
        context={
          autentique.isConfigured
            ? autentique.isSandbox
              ? 'Conta da Westhetics · sandbox, sem validade jurídica.'
              : 'Conta da Westhetics · produção.'
            : 'Sem chave: o envio para assinatura responde 503.'
        }
      >
        <Kpi
          label="Pedidos feitos"
          value={number(autentique.sent)}
          note="criar, reemitir e lembrar"
        />
        <Kpi label="Na fila" value={number(autentique.queued)} note="agora, ainda sem tentativa" />
        <Kpi
          label="Em nova tentativa"
          value={number(autentique.retrying)}
          note={autentique.lastFailureDetail ?? 'sem causa registrada'}
        />
        <Kpi
          label="Falharam"
          value={number(autentique.failed)}
          tone={risk(autentique.failed)}
          note="não chegaram à Autentique"
        />
        <Kpi label="Avisos recebidos" value={number(events.received)} note="no período" />
        <Kpi
          label="Avisos aguardando"
          value={number(events.pending)}
          note="agora; entram no próximo minuto"
        />
        <Kpi
          label="Avisos em nova tentativa"
          value={number(events.retrying)}
          note="voltam até 10 vezes"
        />
        <Kpi
          label="Esgotaram as tentativas"
          value={number(events.exhausted)}
          tone={risk(events.exhausted)}
          note="não foram aplicados"
        />
        <Kpi
          label="Avisos recusados"
          value={number(events.rejected)}
          tone={risk(events.rejected)}
          note="a assinatura do aviso não confere"
        />
      </Front>

      <Front
        title="PDF"
        context={
          pdf.isConfigured
            ? 'Browser Rendering da Cloudflare.'
            : 'PDF sem provedor configurado: nenhum documento sai.'
        }
      >
        <Kpi
          label="Falhas"
          value={number(pdf.failures)}
          tone={risk(pdf.failures)}
          note="PDFs que não saíram"
        />
        <Kpi
          label="Pelo limite de uso"
          value={number(pdf.rateLimited)}
          tone={risk(pdf.rateLimited)}
          note="a Cloudflare respondeu 429"
        />
        <Kpi
          label="Documentos gerados"
          value={number(pdf.generatedDocuments)}
          note="com PDF, no período"
        />
      </Front>

      <Front
        title="E-mail"
        context={email.mode === 'resend' ? 'Resend.' : 'Modo console: nenhum e-mail sai.'}
      >
        <Kpi
          label="Entregues ao Resend"
          value={number(email.delivered)}
          note="os e-mails dos envios"
        />
        <Kpi
          label="Falhas"
          value={number(email.failures)}
          tone={risk(email.failures)}
          note="qualquer e-mail da Westhetics, também os de acesso"
        />
      </Front>

      <Front title="Integrações" context="Webhooks aos sistemas das clínicas, API pública e MCP.">
        <Kpi label="Entregas feitas" value={number(integrations.delivered)} note="no período" />
        <Kpi
          label="Falharam"
          value={number(integrations.failed)}
          tone={risk(integrations.failed)}
          note="depois de 8 tentativas"
        />
        <Kpi label="Aguardando nova tentativa" value={number(integrations.retrying)} note="agora" />
        <Kpi
          label="Webhooks desligados"
          value={number(integrations.disabledWebhooks)}
          tone={risk(integrations.disabledWebhooks)}
          note="por falhar 72 horas"
        />
        <Kpi label="Chamadas" value={number(integrations.requests)} note="à API pública e ao MCP" />
      </Front>

      <Front title="Banco" context="CPU e memória ficam no painel do Supabase.">
        <Kpi
          label="Conexões"
          value={number(database.connections)}
          tone={connectionsHigh ? 'critical' : 'default'}
          note={`abertas agora, de ${number(database.maxConnections)} no máximo`}
        />
        <Kpi
          label="Tamanho do banco"
          value={formatBytes(database.sizeBytes)}
          note="dados e índices"
        />
        <Kpi
          label="Arquivos"
          value={formatBytes(database.storageBytes)}
          note={`${number(database.storageObjects)} arquivos guardados`}
        />
      </Front>

      <Front title="Erros da API">
        <Kpi
          label="Erros inesperados"
          value={number(overview.apiErrors)}
          tone={risk(overview.apiErrors)}
          note="respostas 500 no período"
        />
      </Front>
    </section>
  )
}

/** O esqueleto dos blocos do panorama: atenção, rotinas e a grade. */
export function OverviewSkeleton() {
  return (
    <div role="status" aria-label="Carregando o monitoramento" className="flex flex-col gap-6">
      <Skeleton className="h-[96px] w-full" />
      <Skeleton className="h-[184px] w-full" />
      <Skeleton className="h-[240px] w-full" />
    </div>
  )
}
